import React from 'react';
import { View, ActivityIndicator, StyleSheet } from 'react-native';
import { NavigationContainer, DrawerActions } from '@react-navigation/native';
import { createNativeStackNavigator } from '@react-navigation/native-stack';
import { createBottomTabNavigator } from '@react-navigation/bottom-tabs';
import { createDrawerNavigator } from '@react-navigation/drawer';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import { theme } from '../constants/theme';
import { useAuth } from '../contexts/AuthContext';
import CustomDrawerContent from '../components/CustomDrawerContent';

// Import screens
import LoginScreen from '../screens/LoginScreen';
import ForgotPasswordScreen from '../screens/ForgotPasswordScreen';
import DashboardScreen from '../screens/DashboardScreen';
import CowListScreen from '../screens/CowListScreen';
import CowDetailScreen from '../screens/CowDetailScreen';
import AddEditCowScreen from '../screens/AddEditCowScreen';
import AddCowRecordScreen from '../screens/AddCowRecordScreen';
import DairyRecordsScreen from '../screens/DairyRecordsScreen';
import AddDairyRecordScreen from '../screens/AddDairyRecordScreen';
import ReportsScreen from '../screens/ReportsScreen';
import ActivityLogScreen from '../screens/ActivityLogScreen';
import CustomerListScreen from '../screens/CustomerListScreen';
import AddEditCustomerScreen from '../screens/AddEditCustomerScreen';
import CustomerAccountScreen from '../screens/CustomerAccountScreen';
import CustomerDetailScreen from '../screens/CustomerDetailScreen';
import AddCustomerTransactionScreen from '../screens/AddCustomerTransactionScreen';
import DairyPurchasesScreen from '../screens/DairyPurchasesScreen';
import AddEditDairyPurchaseScreen from '../screens/AddEditDairyPurchaseScreen';
import EmployeeListScreen from '../screens/EmployeeListScreen';
import AddEditEmployeeScreen from '../screens/AddEditEmployeeScreen';
import EmployeeDetailScreen from '../screens/EmployeeDetailScreen';
import AddSalaryPaymentScreen from '../screens/AddSalaryPaymentScreen';
import ChangePasswordScreen from '../screens/ChangePasswordScreen';

const Tab = createBottomTabNavigator();
const Stack = createNativeStackNavigator();
const Drawer = createDrawerNavigator();

const TabNavigator = () => {
    return (
        <Tab.Navigator
            screenOptions={{
                headerShown: false,
                tabBarActiveTintColor: theme.colors.primary,
                tabBarInactiveTintColor: theme.colors.textSecondary,
                tabBarStyle: {
                    backgroundColor: theme.colors.surface,
                    borderTopColor: theme.colors.border,
                    paddingBottom: 5,
                    height: 60,
                },
                tabBarLabelStyle: {
                    fontSize: 12,
                    fontWeight: '600',
                },
            }}>
            <Tab.Screen
                name="Dashboard"
                component={DashboardScreen}
                options={{
                    tabBarIcon: ({ color, size }) => (
                        <Icon name="home" size={size} color={color} />
                    ),
                }}
            />
            <Tab.Screen
                name="Cows"
                component={CowListScreen}
                options={{
                    tabBarIcon: ({ color, size }) => (
                        <Icon name="cow" size={size} color={color} />
                    ),
                }}
            />
            <Tab.Screen
                name="Customers"
                component={CustomerListScreen}
                options={{
                    tabBarIcon: ({ color, size }) => (
                        <Icon name="account-group" size={size} color={color} />
                    ),
                }}
            />
            <Tab.Screen
                name="Reports"
                component={ReportsScreen}
                options={{
                    tabBarIcon: ({ color, size }) => (
                        <Icon name="chart-line" size={size} color={color} />
                    ),
                }}
            />
            {/* <Tab.Screen
                name="Profile"
                component={DashboardScreen}
                listeners={({ navigation }) => ({
                    tabPress: (e) => {
                        e.preventDefault();
                        navigation.dispatch(DrawerActions.openDrawer());
                    },
                })}
                options={{
                    tabBarIcon: ({ color, size }) => (
                        <Icon name="account-circle" size={size} color={color} />
                    ),
                }}
            /> */}
        </Tab.Navigator>
    );
};

const DrawerNavigator = () => {
    return (
        <Drawer.Navigator
            drawerContent={(props) => <CustomDrawerContent {...props} />}
            screenOptions={{
                headerShown: false,
                drawerType: 'front',
                drawerStyle: {
                    width: 280,
                },
            }}>
            <Drawer.Screen name="Main" component={TabNavigator} />
        </Drawer.Navigator>
    );
};

const MainNavigator = () => {
    return (
        <Stack.Navigator
            screenOptions={{
                headerStyle: {
                    backgroundColor: theme.colors.primary,
                },
                headerTintColor: theme.colors.surface,
                headerTitleStyle: {
                    fontWeight: '700',
                },
                headerTitleAlign: 'center',
            }}>
            <Stack.Screen
                name="Home"
                component={DrawerNavigator}
                options={{ headerShown: false }}
            />
            <Stack.Screen
                name="CowDetail"
                component={CowDetailScreen}
                options={{ headerShown: false }}
            />
            <Stack.Screen
                name="AddEditCow"
                component={AddEditCowScreen}
                options={{ headerShown: false }}
            />
            <Stack.Screen
                name="AddCowRecord"
                component={AddCowRecordScreen}
                options={{ headerShown: false }}
            />
            <Stack.Screen
                name="AddDairyRecord"
                component={AddDairyRecordScreen}
                options={({ route }: any) => ({
                    title: route.params?.existingRecord ? 'Edit Dairy Record' : 'Add Dairy Record',
                })}
            />
            <Stack.Screen
                name="ActivityLog"
                component={ActivityLogScreen}
                options={{ headerShown: false }}
            />
            <Stack.Screen
                name="CustomerList"
                component={CustomerListScreen}
                options={{ headerShown: false }}
            />
            <Stack.Screen
                name="AddEditCustomer"
                component={AddEditCustomerScreen}
                options={{ headerShown: false }}
            />
            <Stack.Screen
                name="CustomerAccount"
                component={CustomerAccountScreen}
                options={{ headerShown: false }}
            />
            <Stack.Screen
                name="CustomerDetail"
                component={CustomerDetailScreen}
                options={{ headerShown: false }}
            />
            <Stack.Screen
                name="AddCustomerTransaction"
                component={AddCustomerTransactionScreen}
                options={{ headerShown: false }}
            />
            <Stack.Screen
                name="DairyPurchases"
                component={DairyPurchasesScreen}
                options={{ headerShown: false }}
            />
            <Stack.Screen
                name="AddEditDairyPurchase"
                component={AddEditDairyPurchaseScreen}
                options={{ headerShown: false }}
            />
            <Stack.Screen
                name="EmployeeList"
                component={EmployeeListScreen}
                options={{ headerShown: false }}
            />
            <Stack.Screen
                name="AddEditEmployee"
                component={AddEditEmployeeScreen}
                options={{ headerShown: false }}
            />
            <Stack.Screen
                name="EmployeeDetail"
                component={EmployeeDetailScreen}
                options={{ headerShown: false }}
            />
            <Stack.Screen
                name="AddSalaryPayment"
                component={AddSalaryPaymentScreen}
                options={{ headerShown: false }}
            />
            <Stack.Screen
                name="ChangePassword"
                component={ChangePasswordScreen}
                options={{ headerShown: false }}
            />
        </Stack.Navigator>
    );
};

const AuthNavigator = () => {
    return (
        <Stack.Navigator
            screenOptions={{
                headerShown: false,
            }}>
            <Stack.Screen name="Login" component={LoginScreen} />
            <Stack.Screen
                name="ForgotPassword"
                component={ForgotPasswordScreen}
            // options={{
            //     headerShown: true,
            //     headerStyle: {
            //         backgroundColor: theme.colors.primary,
            //     },
            //     headerTintColor: theme.colors.surface,
            //     headerTitleStyle: {
            //         fontWeight: '700',
            //     },
            //     title: 'Forgot Password',
            // }}
            />
        </Stack.Navigator>
    );
};

const AppNavigator = () => {
    const { isAuthenticated, isLoading } = useAuth();

    if (isLoading) {
        return (
            <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={theme.colors.primary} />
            </View>
        );
    }

    return (
        <NavigationContainer>
            {isAuthenticated ? <MainNavigator /> : <AuthNavigator />}
        </NavigationContainer>
    );
};

const styles = StyleSheet.create({
    loadingContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: theme.colors.background,
    },
});

export default AppNavigator;
